/**
 * Common Utilities
 * General purpose utility functions
 */

import { Avatar, notification, Tag } from 'antd';
import {
  IconWarning,
  IconAnalytics,
  IconBell,
  IconCourse,
  IconCalendar,
  IconParentLink,
  IconError,
  IconExport,
  IconDocument,
  IconLock,
  IconLogout,
  IconMessage,
  IconSettings,
  IconUsers,
  IconUser
} from '../../shared/components/icons';
import { Link } from 'react-router-dom';
import { handleLogout } from '../auth/authUtils';
import { disablePushNotifications } from '../pwa';
import { getApiFileUrl } from './fileUtils';
import i18n from '../i18n';

// ================== ID GENERATION ==================

/**
 * Generate a temporary unique ID
 * @returns {string}
 */
export const generateTempId = () =>
  `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

// ================== GREETING ==================

/**
 * Get time-based greeting (translated)
 * @returns {string}
 */
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return i18n.t('dates.goodMorning');
  if (hour < 17) return i18n.t('dates.goodAfternoon');
  return i18n.t('dates.goodEvening');
};

/**
 * Get relative time ago string (translated)
 * @param {string|Date} date - The date to compare
 * @returns {string}
 */
export const getTimeAgo = (date) => {
  const now = new Date();
  const updateDate = new Date(date);
  const diffInHours = Math.floor((now - updateDate) / (1000 * 60 * 60));

  if (diffInHours < 1) return i18n.t('dates.justNow');
  if (diffInHours < 24) return i18n.t('dates.hoursAgo', { count: diffInHours });
  if (diffInHours < 168) return i18n.t('dates.daysAgo', { count: Math.floor(diffInHours / 24) });
  return updateDate.toLocaleDateString();
};

// ================== ROLE HELPERS ==================

/**
 * Role icon component
 */
export const RoleIcon = ({ role }) => {
  if (role === 'TEACHER') return <span>👨‍🏫</span>;
  if (role === 'ADMIN') return <span>🛠️</span>;
  return <span>👨‍🎓</span>; // default = student
};

/**
 * Audience options for announcements, and the styling of their tags.
 *
 * Doubles as the source of the target-audience picker, so anything added here
 * becomes a choice an admin can publish to. PARENT is one even though no parent
 * ever signs in: it is what the server reads to decide whether an announcement
 * is forwarded to the parent's Telegram.
 */
export const roleConfig = {
  ALL: { color: 'var(--color-fn-majolica)', icon: <IconUsers />, labelKey: 'updates.targets.ALL' },
  TEACHER: { color: 'var(--color-fn-pistachio)', icon: <IconCourse />, labelKey: 'updates.targets.TEACHER' },
  STUDENT: { color: 'var(--color-fn-saffron)', icon: <IconUser />, labelKey: 'updates.targets.STUDENT' },
  PARENT: { color: 'var(--color-fn-plum)', icon: <IconParentLink />, labelKey: 'updates.targets.PARENT' },
};

/**
 * Convert role array to display tags
 * @param {string[]} roles - Array of role strings
 * @returns {JSX.Element | null}
 */
export const convertRolesToTargetTag = (roles) => {
  if (!roles || roles.length === 0) return null;

  // ADMIN only ever lands in this list through the "All users" expansion —
  // admins are not a pickable audience — so its presence marks a broadcast to
  // everyone and is shown as a single "All users" tag rather than a row of them.
  if (roles.includes('ADMIN')) {
    const cfg = roleConfig.ALL;
    return (
      <Tag icon={cfg.icon} color={cfg.color}>
        {i18n.t('updates.targets.ALL')}
      </Tag>
    );
  }

  // otherwise → render each selected audience as its own tag (e.g. a
  // students-and-teachers announcement shows both).
  return roles.map((role) => {
    const cfg = roleConfig[role];
    return (
      <Tag key={role} icon={cfg?.icon} color={cfg?.color}>
        {/* A role with no entry above can still arrive from the API; show its
            name rather than an untranslated key. */}
        {i18n.t(`updates.targets.${role}`, { defaultValue: role })}
      </Tag>
    );
  });
};

// ================== UPDATE TYPES ==================

/**
 * Update type configuration for styling.
 *
 * `tone` is the same hue as `color`, named rather than resolved, because
 * `IconTile` takes a tone and cannot be handed a `var()`. The two must not
 * drift: a NOTICE is garnet in a tile, in a tag and in the modal.
 */
export const updateTypeConfig = {
  NEWS: { icon: <IconDocument />, color: 'var(--color-fn-majolica)', tone: 'majolica', labelKey: 'updates.types.NEWS' },
  ANNOUNCEMENT: { icon: <IconBell />, color: 'var(--color-fn-terracotta)', tone: 'terracotta', labelKey: 'updates.types.ANNOUNCEMENT' },
  EVENT: { icon: <IconCalendar />, color: 'var(--color-fn-pistachio)', tone: 'pistachio', labelKey: 'updates.types.EVENT' },
  NOTICE: { icon: <IconError />, color: 'var(--color-fn-garnet)', tone: 'garnet', labelKey: 'updates.types.NOTICE' },
  REPORT: { icon: <IconAnalytics />, color: 'var(--color-fn-plum)', tone: 'plum', labelKey: 'updates.types.REPORT' },
  INFO: { icon: <IconDocument />, color: 'var(--color-fn-majolica)', tone: 'majolica', labelKey: 'updates.types.INFO' },
  WARNING: { icon: <IconWarning />, color: 'var(--color-fn-saffron)', tone: 'saffron', labelKey: 'updates.types.WARNING' },
  ALERT: { icon: <IconError />, color: 'var(--color-fn-garnet)', tone: 'garnet', labelKey: 'updates.types.ALERT' }
};

const LESSON_CANCELLATION_REASON_KEYS = {
  TEACHER_ILLNESS: 'lesson.cancelReasonTeacherIllness',
  BAD_WEATHER: 'lesson.cancelReasonBadWeather',
  HOLIDAY: 'lesson.cancelReasonHoliday',
  TECHNICAL_ISSUES: 'lesson.cancelReasonTechnicalIssues',
  CONTENT_NOT_RELEVANT: 'lesson.cancelReasonContentNotRelevant',
  CONTENT_NO_LONGER_RELEVANT: 'lesson.cancelReasonContentNotRelevant',
  MERGED_LESSON: 'lesson.cancelReasonMergedLesson',
  MERGED_WITH_ANOTHER_LESSON: 'lesson.cancelReasonMergedLesson',
  OTHER: 'lesson.cancelReasonOther',
};

/**
 * Translate a lesson cancellation reason stored as either a stable code
 * or a legacy English display value.
 */
export const translateLessonCancellationReason = (reason, t = i18n.t.bind(i18n)) => {
  if (!reason) return '';

  const normalized = String(reason).trim();
  if (!normalized) return '';

  const normalizedKey = normalized
    .replace(/&/g, 'AND')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

  const translationKey = LESSON_CANCELLATION_REASON_KEYS[normalizedKey];
  return translationKey ? t(translationKey) : normalized;
};

// ================== NOTIFICATIONS ==================

/**
 * Show Ant Design notification for new message
 * @param {Object} message - Message object
 */
export const showAntNotification = (message) => {
  notification.open({
    message: `New message from @${message.senderUsername}`,
    description: message.content,
    icon: <IconMessage style={{ color: 'var(--color-fn-majolica)' }} />,
    duration: 4
  });
};

/**
 * Play notification sound
 */
export const playSendSound = () => {
  const audio = new Audio('/sounds/notify.mp3');
  audio.play().catch(err => {
    console.warn('Send sound blocked:', err);
  });
};

// ================== DROPDOWN MENUS ==================

/**
 * Chat dropdown menu items
 */
export const chatDropdownItems = [
  { key: '1', label: 'View Profile' },
  { key: '2', label: 'Clear Chat', danger: true },
  { key: '3', label: 'Block User', danger: true }
];

/**
 * Profile path resolver based on role.
 */
const profilePathForRole = (role) => {
  switch (role) {
    case 'ADMIN':
      return '/admin/profile';
    case 'TEACHER':
      return '/teacher/profile';
    case 'STUDENT':
      return '/student/profile';
    default:
      return '/settings';
  }
};

/**
 * User dropdown menu configuration
 * @param {Function} navigate - React Router navigate function
 * @param {Function} t - Translation function
 * @param {Object} options - { user } and other action callbacks
 * @returns {Array}
 */
export const dropdownMenu = (navigate, t, options = {}) => {
  const { user } = options;
  const fullName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
    : '';
  const roleLabel = user?.roleName
    ? (t ? t(`roles.${user.roleName.toLowerCase()}`) : user.roleName)
    : '';
  const photoUrl = user?.profilePhotoUrl ? getApiFileUrl(user.profilePhotoUrl) : null;
  const initials = user
    ? `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.trim().toUpperCase()
    : '';
  const profilePath = profilePathForRole(user?.roleName);

  const items = [];

  if (user) {
    items.push({
      key: 'profile-header',
      label: (
        <Link to={profilePath} className="lms-account-dropdown__header">
          <Avatar
            size={40}
            src={photoUrl || undefined}
            icon={!photoUrl && !initials ? <RoleIcon role={user.roleName} /> : null}
            className="lms-account-dropdown__avatar"
          >
            {!photoUrl && initials ? initials : null}
          </Avatar>
          <span className="lms-account-dropdown__identity">
            <span className="lms-account-dropdown__name">{fullName || user.username}</span>
            {roleLabel && (
              <span className="lms-account-dropdown__role">{roleLabel}</span>
            )}
          </span>
        </Link>
      ),
    });
    items.push({ type: 'divider' });
  }

  return [
    ...items,
    {
      key: '1',
      label: (
        // Theme and language live at /settings and nothing in the shell says so;
        // the hint signposts them (app-shell.md §3).
        <Link to="/settings" className="lms-account-dropdown__item">
          <span className="lms-account-dropdown__item-main">
            <IconSettings />&nbsp;&nbsp;{t ? t('menu.settings') : 'Settings'}
          </span>
          <span className="lms-account-dropdown__item-hint">
            {t ? `${t('settings.theme')} · ${t('settings.language')}` : 'Theme · Language'}
          </span>
        </Link>
      )
    },
    // {
    //   key: '2',
    //   disabled: true,
    //   label: (
    //     <span style={{ opacity: 0.5 }}>
    //       <IconSupport />&nbsp;&nbsp;{t ? t('menu.helpSupport') : 'Help & Support'}&nbsp;&nbsp;<IconLock style={{ fontSize: 11 }} />
    //     </span>
    //   ),
    // },
    // {
    //   key: '3',
    //   disabled: true,
    //   label: (
    //     <span style={{ opacity: 0.5 }}>
    //       <IconWarning />&nbsp;&nbsp;{t ? t('menu.feedback') : 'Feedback / Report Issue'}&nbsp;&nbsp;<IconLock style={{ fontSize: 11 }} />
    //     </span>
    //   ),
    // },
    {
      key: '4',
      disabled: true,
      label: (
        <span className="lms-account-dropdown__disabled">
          <IconExport />&nbsp;&nbsp;{t ? t('menu.exportData') : 'Export Data'}&nbsp;&nbsp;<IconLock className="lms-account-dropdown__lock" />
        </span>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: '5',
      label: (
        <span>
          <IconLogout />&nbsp;&nbsp;{t ? t('menu.logout') : 'Log out'}
        </span>
      ),
      danger: true,
      onClick: () => handleLogout(navigate, { beforeLogout: () => disablePushNotifications() }),
    }
  ];
};

// ================== DATE FORMATTING (CHAT-SPECIFIC) ==================

/**
 * Format date separator for chat
 * @param {string|Date} timestamp
 * @returns {string}
 */
export const formatDateSeparator = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  if (isSameDay(date, today)) return i18n.t('dates.today');
  if (isSameDay(date, yesterday)) return i18n.t('dates.yesterday');

  const lang = i18n.language || 'uz';
  const localeMap = { ru: 'ru-RU', en: 'en-US' };
  const locale = localeMap[lang] || 'en-US';

  const UZ_MONTHS_LONG = [
    'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
    'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
  ];

  if (lang === 'uz') {
    const day = date.getDate();
    const month = UZ_MONTHS_LONG[date.getMonth()];
    if (date.getFullYear() === today.getFullYear()) {
      return `${day} ${month}`;
    }
    return `${day} ${month}, ${date.getFullYear()}`;
  }

  const options =
    date.getFullYear() === today.getFullYear()
      ? { month: 'long', day: 'numeric' }
      : { year: 'numeric', month: 'long', day: 'numeric' };

  return date.toLocaleDateString(locale, options);
};

// ================== MESSAGE HELPERS ==================

// ================== FILE SIZE FORMATTING ==================

/**
 * Formats file size in bytes to a human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// ================== MESSAGE PREVIEW ==================

/**
 * Build the short preview string for the conversation sidebar from a message.
 * Voice / image / file / video messages use a typed placeholder because their
 * `content` column stores opaque metadata (e.g. JSON {fileId,duration} for voice).
 */
export const formatMessagePreview = (message) => {
  if (!message) return '';
  switch (message.messageType) {
    case 'VOICE_MESSAGE': return '🎤 Voice message';
    case 'IMAGE':         return '🖼️ Photo';
    case 'FILE':          return '📎 File';
    case 'VIDEO':         return '🎬 Video';
    default:              return message.content ?? '';
  }
};

// ================== OPTIMISTIC MESSAGE HELPERS ==================

/**
 * Replace optimistic message with real message from server
 * @param {Function} setMessages - State setter
 * @param {Object} newMessage - New message from server
 * @param {Function} setSelfLastReadMessageId - State setter
 * @param {Function} setFirstUnreadIndex - State setter
 */
export const replaceOptimisticMessage = (setMessages, newMessage, setSelfLastReadMessageId, setFirstUnreadIndex) => {
  setMessages((prev) => {
    let idx = -1;

    if (newMessage.tempId) {
      idx = prev.findIndex((m) => m.id === newMessage.tempId);
    }

    if (idx === -1) {
      const candidates = prev
        .map((msg, index) => ({ msg, index }))
        .filter(({ msg }) =>
          msg.isOptimistic &&
          msg.senderId === newMessage.senderId &&
          msg.content?.trim() === newMessage.content?.trim()
        );

      if (candidates.length > 0) {
        idx = candidates[candidates.length - 1].index;
      }
    }

    if (idx === -1) {
      const candidates = prev
        .map((msg, index) => ({ msg, index }))
        .filter(({ msg }) =>
          msg.isOptimistic &&
          msg.senderId === newMessage.senderId
        );

      if (candidates.length > 0) {
        idx = candidates[candidates.length - 1].index;
      }
    }

    if (idx !== -1) {
      const updated = [...prev];
      updated[idx] = {
        ...newMessage,
        isRead: false,
        isOptimistic: false
      };
      setSelfLastReadMessageId(newMessage.id);
      setFirstUnreadIndex(updated.length);
      return updated;
    }

    const existingMessage = prev.find(m =>
      m.id === newMessage.id ||
      (!m.isOptimistic && m.content === newMessage.content && m.senderId === newMessage.senderId)
    );

    if (existingMessage) {
      return prev;
    }

    setSelfLastReadMessageId(newMessage.id);
    return [...prev, { ...newMessage, isRead: false, isOptimistic: false }];
  });
};

